<template>
  <div class="deliveryProgress">
    <card-m
      :title="title"
      cardWidth="100%"
      cardHeight="100%"
      maxHeight="unset"
      toolWidth="70%"
      toolJustifyontent="flex-start"
      :show-filter="false"
      :show-export="true"
      :show-column-edit="true"
      export-file-name="交付列表"
      @export="handleExport"
      @edit-column="openColumnEdit"
    >
      <!-- ⚠️ 交付主页不提供「批量导入 / 批量备注 / 问题列表」按钮，工具区留空 -->
      <template slot="card-box">
        <card-table
          ref="cardTableRef"
          tableID="deliveryTable"
          :key="tableRenderKey"
          :total="totalCounts"
          :currentPage="currentPage"
          :tableColumn="effectiveVisibleColumns"
          :tableData="deliveryData"
          :mobileColumns="mobileColumnEditFilter"
          :loading="loading"
          :showIndex="true"
          :showPaging="true"
          :size="pageSize"
          @sizeChange="handleSizeChanges"
          @currentChange="handleCurrentChanges"
          @column-filter-change="onColumnFilterChange"
          :columnFilterValues="columnFilterValues"
          @column-width-change="onColumnWidthChange"
        />
      </template>
    </card-m>

    <!-- 列编辑弹窗：电脑端偏好存 deliveryProgress，手机端由 mixin 派生 deliveryProgress_m -->
    <column-edit-dialog
      :visible.sync="columnEditVisible"
      :columns="tempOrderedColumns"
      :defaultColumns="effectiveDefaultColumnDefs"
      :title="columnEditTitle"
      :dialogWidth="columnEditDialogWidth"
      @confirm="handleColumnSave"
      @cancel="handleColumnCancel"
    />
  </div>
</template>

<script>
import { getProductHomeList } from '@/api/mainapi.js';
import columnEditMixin from '@/mixins/columnEditMixin';
import ColumnEditDialog from '@/components/ColumnEditDialog.vue';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';

export default {
  name: 'deliveryProgress',
  mixins: [columnEditMixin],
  components: { ColumnEditDialog },
  props: {
    title: {
      type: String,
      default: '交付主页'
    }
  },

  data() {
    return {
      /* ✅ 列偏好页面标识：电脑端 deliveryProgress / 手机端 deliveryProgress_m */
      pageType: 'deliveryProgress',

      /* ✅ 后端交付接口还没做出来，暂时复用产品列表接口；
         表格也先只显示「产品名称」「产品型号」两列。 */
      deliveryColumns: [
        { prop: 'name', label: '产品名称', filterable: true },
        { prop: 'model', label: '产品型号', filterable: true }
      ],
      // 手机端默认显示 / 可编辑的列（与电脑端一致，就这两列）
      mobileColumnFilter: ['name', 'model'],
      mobileColumnEditFilter: ['name', 'model'],

      // 列筛选条件（只用到这两列）
      columnFilterValues: {},

      // 分页
      currentPage: 1,
      pageSize: 50,

      // 数据
      deliveryData: [],
      totalCounts: 0,
      loading: false
    };
  },

  computed: {
    // 供 columnEditMixin 使用的列定义
    baseColumnDefs() {
      return this.deliveryColumns;
    }
  },

  async mounted() {
    await this.loadColumnPref();
    this.getDeliveryList();
  },

  methods: {
    /** 获取交付列表
     *  ⚠️ 后端交付接口还没做出来，这里先复用产品列表接口 getProductHomeList，
     *     请求体与 productProgress 保持一致（只用 name / model 两个筛选条件，其余留空）。 */
    async getDeliveryList() {
      this.loading = true;

      const body = {
        pageNo: String(this.currentPage),
        pageSize: String(this.pageSize),

        serialCode: '',
        fsn: '',
        name: this.columnFilterValues['name'] || '',
        nameNot: '',
        model: this.columnFilterValues['model'] || '',
        taskNo: '',
        processStatus: '',
        executeStatus: '',
        productCreateTimeStart: '',
        productCreateTimeEnd: '',
        parameter: '',
        productDetail: '',
        productionDepartment: '',
        isIssue: 'true'
      };

      try {
        const res = await getProductHomeList(body);
        this.loading = false;

        if (res.data.code === 200) {
          // ✅ 兼容两种返回结构：
          //    { total, productList: [...] }（productProgress 当前用的）
          //    { productList: { total, list: [...] } }
          const payload = (res.data && res.data.data) || {};
          const raw = payload.productList;
          const list = Array.isArray(raw) ? raw : (raw && raw.list) || [];
          const total = payload.total || (raw && raw.total) || 0;

          this.totalCounts = Number(total) || 0;
          this.deliveryData = this.processDeliveryData(list);
        } else {
          this.$message.error(res.data.message || '获取交付列表失败');
        }
      } catch (error) {
        this.loading = false;
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 只保留要展示的两列（后端换成交付接口后，在这里替换字段映射即可） */
    processDeliveryData(list) {
      return (list || []).map(item => ({
        name: item.name || '',
        model: item.model || '',
        _raw: item
      }));
    },

    /** 列筛选变化 */
    onColumnFilterChange(filterValues) {
      this.columnFilterValues = filterValues;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getDeliveryList();
    },

    /** 分页 */
    handleSizeChanges(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getDeliveryList();
    },

    handleCurrentChanges(val) {
      this.currentPage = val;
      this.getDeliveryList();
    },

    /** 导出 Excel */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.table_to_book(
        document.querySelector('#deliveryTable'),
        xlsxParam
      );
      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      });
      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          '交付列表.xlsx'
        );
      } catch (e) {
        // 导出失败时静默处理
      }
      return wbout;
    }
  }
};
</script>

<style scoped lang="scss">
.deliveryProgress {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

::v-deep .card-m {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

::v-deep .card-box {
  flex: 1;
  min-height: 0;
  overflow-y: hidden;
  overflow-x: hidden;
}

/* 手机端 */
@media (max-width: 768px) {
  .deliveryProgress {
    height: 100%;
  }
}
</style>
